import { useEffect, useMemo, useRef, useState } from 'react'
import { apiGet, apiPost } from '../utils/api'
import { useTenant } from '../tenant/TenantContext'
import { useToasts } from './Toasts'
import { usePerms } from '../auth/PermsContext'
import { useI18n } from '../i18n'
import Confirm from './parts/Confirm'

type StatefulRuleDraft = {
  id: number
  type: string
  description: string
  mailbox: string
  tag: string
  keepLast: string
  keepWithin: string
}

type StatefulRetentionRule = {
  type?: string
  description?: string
  mailboxes?: string[]
  tags?: string[]
  options?: Record<string, any>
}

type StatefulRetentionConfig = { rules?: StatefulRetentionRule[] } | null | undefined

type Policy = {
  id: number
  name: string
  schedule: string
  retention: string
  backup_strategy?: string
  full_backup_interval?: number
  max_non_full_backups?: number
  stateful_retention?: StatefulRetentionConfig
  dynamic_retention?: { rules?: any[] }
  freeze_windows?: Array<Record<string, any>>
}

type PolicyKpiSummary = {
  policy_id: number
  schedule_id?: number | null
  rule_id?: string | null
  rule_type?: string | null
  rule_label?: string | null
  message?: string | null
  actions?: string[] | null
  severity?: string | null
  triggered_at?: string | null
}

type KpiRuleDraft = {
  id: number
  type: 'size_change' | 'failure_streak' | 'last_full_age'
  label: string
  severity: 'info' | 'warning' | 'critical'
  thresholdPercent: string
  window: string
  direction: 'increase' | 'decrease' | 'both'
  threshold: string
  resumeMinutes: string
  maxAgeHours: string
  cooldownMinutes: string
  actionForceFull: boolean
  actionPause: boolean
  actionNotify: boolean
  actionExtendKeep: boolean
  extendKeepMin: string
  extendKeepIncrease: string
  extendKeepDuration: string
}

type FreezeWindowDraft = {
  id: number
  days: string
  start: string
  end: string
}

const STATEFUL_TARGET_TYPES = [
  { value: 'imap', label: 'IMAP' },
  { value: 'directory', label: 'Directory' },
  { value: 'nextcloud', label: 'Nextcloud' },
]

const KPI_RULE_LABELS: Record<KpiRuleDraft['type'], string> = {
  size_change: 'Variazione dimensione',
  failure_streak: 'Failure streak',
  last_full_age: 'Età ultimo full',
}

const KPI_DIRECTION_OPTIONS: Array<{ value: KpiRuleDraft['direction']; label: string }> = [
  { value: 'increase', label: 'Aumenti' },
  { value: 'decrease', label: 'Riduzioni' },
  { value: 'both', label: 'Entrambe' },
]

const KPI_SEVERITY_OPTIONS: Array<{ value: KpiRuleDraft['severity']; label: string }> = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
]

const splitInputList = (value: string): string[] =>
  value
    .split(/[,;\n]+/)
    .map(part => part.trim())
    .filter(Boolean)

const summarizeStatefulRetention = (config: StatefulRetentionConfig, fallback: string): string => {
  const rules = config?.rules ?? []
  if (!rules.length) return fallback
  return rules
    .map(rule => {
      const segments: string[] = []
      if (rule.type) segments.push(rule.type)
      if (rule.description) segments.push(rule.description)
      const selectors: string[] = []
      if (rule.mailboxes?.length) selectors.push(rule.mailboxes.join(', '))
      if (rule.tags?.length) selectors.push(rule.tags.map(tag => `#${tag}`).join(', '))
      if (selectors.length) segments.push(selectors.join(' | '))
      const opts = rule.options || {}
      const optParts: string[] = []
      if (opts['keep-last']) optParts.push(`keep-last=${opts['keep-last']}`)
      if (opts['keep-within']) optParts.push(`keep-within=${opts['keep-within']}`)
      if (opts['keep-tag']) {
        const keepTag = Array.isArray(opts['keep-tag']) ? opts['keep-tag'].join(', ') : opts['keep-tag']
        optParts.push(`keep-tag=${keepTag}`)
      }
      if (optParts.length) segments.push(optParts.join(', '))
      return segments.join(' · ')
    })
    .join('; ')
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value ?? '-'
  }
}

const kpiBadgeClass = (severity?: string | null) => {
  switch ((severity || '').toLowerCase()) {
    case 'critical':
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200'
    case 'warning':
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-100'
    case 'success':
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-100'
    default:
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200'
  }
}

export default function Policies() {
  const { t } = useI18n()
  const { current } = useTenant()
  const tenantId = current?.id!
  const { push } = useToasts()
  const { has } = usePerms()
  const [items, setItems] = useState<Policy[]>([])
  const [kpiSummaries, setKpiSummaries] = useState<PolicyKpiSummary[]>([])
  const [name, setName] = useState('default')
  const [schedule, setSchedule] = useState('daily@02:30')
  const [retention, setRetention] = useState('GFS 7/4/6')
  const [backupStrategy, setBackupStrategy] = useState<'full' | 'incremental' | 'differential' | 'log'>('full')
  const [maxNonFull, setMaxNonFull] = useState('0')
  const [fullInterval, setFullInterval] = useState('0')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [confirmId, setConfirmId] = useState<number | undefined>(undefined)
  const [statefulRules, setStatefulRules] = useState<StatefulRuleDraft[]>([])
  const ruleCounter = useRef(0)
  const [kpiRules, setKpiRules] = useState<KpiRuleDraft[]>([])
  const [kpiEvaluationInterval, setKpiEvaluationInterval] = useState('')
  const kpiCounter = useRef(0)
  const [freezeWindows, setFreezeWindows] = useState<FreezeWindowDraft[]>([])
  const freezeCounter = useRef(0)

  const defaultsForKpiType = (type: KpiRuleDraft['type']): Partial<KpiRuleDraft> => {
    if (type === 'size_change') {
      return {
        thresholdPercent: '50',
        window: '1',
        direction: 'increase',
        threshold: '',
        actionForceFull: true,
        actionPause: false,
      }
    }
    if (type === 'failure_streak') {
      return {
        threshold: '3',
        thresholdPercent: '',
        actionForceFull: false,
        actionPause: true,
        resumeMinutes: '60',
      }
    }
    return {
      maxAgeHours: '72',
      actionForceFull: true,
      actionPause: false,
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      if (tenantId) {
        const policies = await apiGet(`/tenants/${tenantId}/policies`)
        setItems(policies)
        try {
          const summaries = await apiGet(`/tenants/${tenantId}/policies/kpi-summary`)
          setKpiSummaries(Array.isArray(summaries) ? summaries : [])
        } catch {
          setKpiSummaries([])
        }
        setError(undefined)
      }
    } catch (e: any) {
      setError(e?.message || 'Load failed')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
  }, [tenantId])

  const summaryByPolicy = useMemo(() => {
    return kpiSummaries.reduce<Record<number, PolicyKpiSummary>>((acc, summary) => {
      if (summary && typeof summary.policy_id === 'number') {
        acc[summary.policy_id] = summary
      }
      return acc
    }, {})
  }, [kpiSummaries])

  const renderPolicyKpiBadge = (policyId: number) => {
    const summary = summaryByPolicy[policyId]
    if (!summary) {
      return <span className="text-xs text-gray-500">{t('policy.kpi.empty') || 'Nessun trigger recente'}</span>
    }
    const label =
      summary.rule_label ||
      summary.rule_id ||
      summary.rule_type ||
      t('policy.kpi.rule_generic') ||
      'Regola KPI'
    const message = summary.message || label
    const actionsHint = summary.actions?.length ? summary.actions.join(', ') : undefined
    const scheduleHint =
      summary.schedule_id && summary.schedule_id > 0
        ? `${t('schedule.table.id') || 'Schedule'} #${summary.schedule_id}`
        : undefined
    return (
      <div className="flex flex-col gap-1 items-start">
        <span className={kpiBadgeClass(summary.severity)}>{message}</span>
        <span className="text-[11px] text-gray-500">
          {formatDateTime(summary.triggered_at)}
          {scheduleHint ? ` · ${scheduleHint}` : ''}
        </span>
        {actionsHint && <span className="text-[11px] text-gray-500">{actionsHint}</span>}
      </div>
    )
  }

  const addRule = () => {
    ruleCounter.current += 1
    setStatefulRules(prev => [
      ...prev,
      {
        id: ruleCounter.current,
        type: 'imap',
        description: '',
        mailbox: '',
        tag: '',
        keepLast: '',
        keepWithin: '',
      },
    ])
  }

  const updateRule = (id: number, patch: Partial<StatefulRuleDraft>) => {
    setStatefulRules(prev => prev.map(rule => (rule.id === id ? { ...rule, ...patch } : rule)))
  }

  const removeRule = (id: number) => {
    setStatefulRules(prev => prev.filter(rule => rule.id !== id))
  }

  const buildStatefulPayload = () => {
    const rules = statefulRules
      .map(rule => {
        const mailboxes = splitInputList(rule.mailbox)
        const tags = splitInputList(rule.tag)
        const description = rule.description.trim()
        const keepLast = Number(rule.keepLast)
        const keepWithin = rule.keepWithin.trim()
        const type = rule.type.trim() || 'imap'
        const payload: Record<string, any> = { type }
        if (description) payload.description = description
        if (mailboxes.length) payload.mailboxes = mailboxes
        if (tags.length) payload.tags = tags
        if (!Number.isNaN(keepLast) && keepLast > 0) payload.keep_last = keepLast
        if (keepWithin) payload.keep_within = keepWithin
        return payload
      })
      .filter(rule => (rule.mailboxes && rule.mailboxes.length) || (rule.tags && rule.tags.length))
    return rules.length ? { rules } : undefined
  }

  const addKpiRule = (type: KpiRuleDraft['type']) => {
    kpiCounter.current += 1
    const id = kpiCounter.current
    const base: KpiRuleDraft = {
      id,
      type,
      label: '',
      severity: 'warning',
      thresholdPercent: '50',
      window: '1',
      direction: 'increase',
      threshold: '3',
      resumeMinutes: '60',
      maxAgeHours: '72',
      cooldownMinutes: '',
      actionForceFull: type !== 'failure_streak',
      actionPause: type === 'failure_streak',
      actionNotify: false,
      actionExtendKeep: false,
      extendKeepMin: '',
      extendKeepIncrease: '',
      extendKeepDuration: '',
    }
    Object.assign(base, defaultsForKpiType(type))
    setKpiRules(prev => [...prev, base])
  }

  const updateKpiRule = (id: number, patch: Partial<KpiRuleDraft>) => {
    setKpiRules(prev => prev.map(rule => (rule.id === id ? { ...rule, ...patch } : rule)))
  }

  const removeKpiRule = (id: number) => {
    setKpiRules(prev => prev.filter(rule => rule.id !== id))
  }

  const buildKpiPayload = () => {
    if (!kpiRules.length) return undefined
    const mapped = kpiRules
      .map(rule => {
        const entry: Record<string, any> = {
          id: rule.label ? rule.label.toLowerCase().replace(/\s+/g, '-') : `rule-${rule.id}`,
          type: rule.type,
          label: rule.label || undefined,
          severity: rule.severity,
        }
        if (rule.cooldownMinutes.trim()) {
          const cooldown = Number(rule.cooldownMinutes)
          if (!Number.isNaN(cooldown) && cooldown > 0) entry.cooldown_minutes = cooldown
        }
        const actions: Array<Record<string, any>> = []
        if (rule.actionForceFull) {
          actions.push({ type: 'force_full' })
        }
        if (rule.actionPause) {
          const action: Record<string, any> = { type: 'pause' }
          if (rule.resumeMinutes.trim()) {
            const resume = Number(rule.resumeMinutes)
            if (!Number.isNaN(resume) && resume > 0) action.resume_minutes = resume
          }
          actions.push(action)
        }
        if (rule.actionExtendKeep) {
          const extend: Record<string, any> = { type: 'extend_keep_last' }
          if (rule.extendKeepMin.trim()) {
            const min = Number(rule.extendKeepMin)
            if (!Number.isNaN(min) && min > 0) extend.min_keep_last = min
          }
          if (rule.extendKeepIncrease.trim()) {
            const inc = Number(rule.extendKeepIncrease)
            if (!Number.isNaN(inc) && inc > 0) extend.increase_by = inc
          }
          if (rule.extendKeepDuration.trim()) {
            const dur = Number(rule.extendKeepDuration)
            if (!Number.isNaN(dur) && dur > 0) extend.duration_hours = dur
          }
          actions.push(extend)
        }
        if (rule.actionNotify) {
          actions.push({ type: 'notify', severity: rule.severity })
        }
        if (!actions.length) {
          actions.push({ type: rule.type === 'failure_streak' ? 'pause' : 'force_full' })
        }
        entry.actions = actions
        if (rule.type === 'size_change') {
          const threshold = Number(rule.thresholdPercent)
          if (Number.isNaN(threshold) || threshold <= 0) return null
          entry.threshold_percent = threshold
          entry.window = Math.max(1, Number(rule.window) || 1)
          entry.direction = rule.direction
        } else if (rule.type === 'failure_streak') {
          const threshold = Number(rule.threshold)
          if (Number.isNaN(threshold) || threshold <= 0) return null
          entry.threshold = threshold
        } else if (rule.type === 'last_full_age') {
          const hours = Number(rule.maxAgeHours)
          if (Number.isNaN(hours) || hours <= 0) return null
          entry.max_age_minutes = Math.max(1, Math.round(hours * 60))
        }
        return entry
      })
      .filter((entry): entry is Record<string, any> => entry !== null)
    if (!mapped.length) return undefined
    const payload: Record<string, any> = { rules: mapped }
    const interval = Number(kpiEvaluationInterval)
    if (!Number.isNaN(interval) && interval > 0) {
      payload.evaluation_interval_minutes = interval
    }
    return payload
  }

  const addFreezeWindow = () => {
    freezeCounter.current += 1
    const id = freezeCounter.current
    setFreezeWindows(prev => [...prev, { id, days: 'mon-fri', start: '18:00', end: '08:00' }])
  }

  const updateFreezeWindow = (id: number, patch: Partial<FreezeWindowDraft>) => {
    setFreezeWindows(prev => prev.map(win => (win.id === id ? { ...win, ...patch } : win)))
  }

  const removeFreezeWindow = (id: number) => {
    setFreezeWindows(prev => prev.filter(win => win.id !== id))
  }

  const buildFreezePayload = () => {
    if (!freezeWindows.length) return undefined
    const windows = freezeWindows
      .map(win => {
        if (!win.start || !win.end) return null
        const days = win.days
          .split(/[, ]+/)
          .map(d => d.trim().toLowerCase())
          .filter(Boolean)
        return {
          days,
          start: win.start,
          end: win.end,
        }
      })
      .filter((win): win is { days: string[]; start: string; end: string } => win !== null)
    return windows.length ? windows : undefined
  }

  const create = async () => {
    try {
      if (!name.trim()) {
        push({ text: 'Name required', kind: 'error' })
        return
      }
      const payload: Record<string, any> = {
        name,
        schedule,
        retention,
        notify: 'errors',
        health_check_weekly: true,
        sample_restore_monthly: true,
      }
      const parsedMaxNonFull = Math.max(0, Number(maxNonFull) || 0)
      const parsedFullInterval = Math.max(0, Number(fullInterval) || 0)
      payload.backup_strategy = backupStrategy
      payload.max_non_full_backups = parsedMaxNonFull
      payload.full_backup_interval = parsedFullInterval
      const statefulPayload = buildStatefulPayload()
      if (statefulPayload) {
        payload.stateful_retention = statefulPayload
      }
      const kpiPayload = buildKpiPayload()
      if (kpiPayload) {
        payload.dynamic_retention = kpiPayload
      }
      const freezePayload = buildFreezePayload()
      if (freezePayload) {
        payload.freeze_windows = freezePayload
      }
      await apiPost(`/tenants/${tenantId}/policies`, payload)
      setName('')
      setBackupStrategy('full')
      setMaxNonFull('0')
      setFullInterval('0')
      setStatefulRules([])
      setKpiRules([])
      setKpiEvaluationInterval('')
      setFreezeWindows([])
      await load()
    } catch (e: any) {
      push({ text: e?.message || 'Create failed', kind: 'error' })
    }
  }

  const statefulSummary = (policy: Policy) =>
    summarizeStatefulRetention(policy.stateful_retention, t('stateful.none'))
  const formatStrategy = (value?: string | null) => {
    const mode = (value || 'full').toLowerCase()
    const key = `policy.strategy.${mode}`
    const translated = t(key)
    if (translated && translated !== key) return translated
    return mode.charAt(0).toUpperCase() + mode.slice(1)
  }

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">{t('titles.policies')}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('help.policies')}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          className="input"
          placeholder={t('ph.name')}
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={!has('policies.write')}
        />
        <input
          className="input"
          placeholder={t('ph.schedule')}
          value={schedule}
          onChange={e => setSchedule(e.target.value)}
          disabled={!has('policies.write')}
        />
        <input
          className="input"
          placeholder={t('ph.retention')}
          value={retention}
          onChange={e => setRetention(e.target.value)}
          disabled={!has('policies.write')}
        />
        <select
          className="input"
          value={backupStrategy}
          onChange={e => setBackupStrategy(e.target.value as typeof backupStrategy)}
          disabled={!has('policies.write')}
          title={t('policy.strategy') || 'Strategy'}
        >
          <option value="full">{t('policy.strategy.full') || 'Full'}</option>
          <option value="differential">{t('policy.strategy.differential') || 'Differential'}</option>
          <option value="incremental">{t('policy.strategy.incremental') || 'Incremental'}</option>
        </select>
        <input
          className="input"
          type="number"
          min="0"
          value={maxNonFull}
          onChange={e => setMaxNonFull(e.target.value)}
          placeholder={t('policy.max_non_full') || 'Max non-full'}
          disabled={!has('policies.write')}
        />
        <input
          className="input"
          type="number"
          min="0"
          value={fullInterval}
          onChange={e => setFullInterval(e.target.value)}
          placeholder={t('policy.full_interval') || 'Full interval'}
          disabled={!has('policies.write')}
        />
        <button className="btn" onClick={create} disabled={!has('policies.write')}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="mr-2"
            aria-hidden="true"
          >
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6Z" />
          </svg>
          {t('buttons.create')}
        </button>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <h3 className="text-sm font-semibold">{t('stateful.heading')}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 max-w-xl">
              {t('stateful.help')}
            </p>
          </div>
          <button
            className="btn-secondary whitespace-nowrap"
            onClick={addRule}
            disabled={!has('policies.write')}
          >
            {t('stateful.add')}
          </button>
        </div>
        {statefulRules.length === 0 ? (
          <p className="text-xs text-gray-500">{t('stateful.none')}</p>
        ) : (
          <div className="space-y-2">
            {statefulRules.map(rule => (
              <div key={rule.id} className="grid gap-2 md:grid-cols-6">
                <select
                  className="input"
                  value={rule.type}
                  onChange={e => updateRule(rule.id, { type: e.target.value })}
                  disabled={!has('policies.write')}
                >
                  {STATEFUL_TARGET_TYPES.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <input
                  className="input"
                  placeholder={t('stateful.description')}
                  value={rule.description}
                  onChange={e => updateRule(rule.id, { description: e.target.value })}
                  disabled={!has('policies.write')}
                />
                <input
                  className="input"
                  placeholder={t('stateful.mailbox')}
                  value={rule.mailbox}
                  onChange={e => updateRule(rule.id, { mailbox: e.target.value })}
                  disabled={!has('policies.write')}
                />
                <input
                  className="input"
                  placeholder={t('stateful.tag')}
                  value={rule.tag}
                  onChange={e => updateRule(rule.id, { tag: e.target.value })}
                  disabled={!has('policies.write')}
                />
                <input
                  className="input"
                  type="number"
                  min="0"
                  placeholder={t('stateful.keep_last')}
                  value={rule.keepLast}
                  onChange={e => updateRule(rule.id, { keepLast: e.target.value })}
                  disabled={!has('policies.write')}
                />
                <div className="flex gap-2">
                  <input
                    className="input"
                    placeholder={t('stateful.keep_within')}
                    value={rule.keepWithin}
                    onChange={e => updateRule(rule.id, { keepWithin: e.target.value })}
                    disabled={!has('policies.write')}
                  />
                  <button
                    type="button"
                    className="btn-secondary px-3"
                    onClick={() => removeRule(rule.id)}
                    disabled={!has('policies.write')}
                    aria-label={t('stateful.remove')}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          {t('stateful.hint')}
        </p>
      </div>

      <div className="mt-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-sm">{t('policy.kpi.title') || 'Automazione KPI'}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 max-w-2xl">
              {t('policy.kpi.help') ||
                'Definisci blocchi condizione/azione per reagire automaticamente ai KPI (dimensioni, fallimenti o età dell’ultimo full).'}
            </p>
          </div>
          <label className="flex flex-col text-xs min-w-[140px]">
            <span className="font-medium mb-1">
              {t('policy.kpi.fields.eval_interval') || 'Intervallo valutazione (min)'}
            </span>
            <input
              className="input"
              value={kpiEvaluationInterval}
              onChange={e => setKpiEvaluationInterval(e.target.value)}
              placeholder="es. 60"
              disabled={!has('policies.write')}
            />
          </label>
        </div>
        {kpiRules.length === 0 ? (
          <p className="text-xs text-gray-500">
            {t('policy.kpi.empty') || 'No KPI rules configured.'}
          </p>
        ) : (
          <div className="space-y-3">
            {kpiRules.map(rule => (
              <div key={rule.id} className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-3">
                <div className="flex flex-wrap gap-3">
                  <label className="flex flex-col text-xs w-36">
                    <span className="font-medium mb-1">{t('policy.kpi.fields.type') || 'Type'}</span>
                    <select
                      className="input"
                      value={rule.type}
                      onChange={e => {
                        const value = e.target.value as KpiRuleDraft['type']
                        updateKpiRule(rule.id, { type: value, ...defaultsForKpiType(value) })
                      }}
                      disabled={!has('policies.write')}
                    >
                      <option value="size_change">{t('policy.kpi.type.size') || 'Size change'}</option>
                      <option value="failure_streak">{t('policy.kpi.type.failure') || 'Failure streak'}</option>
                      <option value="last_full_age">{t('policy.kpi.type.full_age') || 'Last full age'}</option>
                    </select>
                  </label>
                  <label className="flex flex-col text-xs flex-1 min-w-[140px]">
                    <span className="font-medium mb-1">{t('policy.kpi.fields.label') || 'Label'}</span>
                    <input
                      className="input"
                      value={rule.label}
                      onChange={e => updateKpiRule(rule.id, { label: e.target.value })}
                      disabled={!has('policies.write')}
                      placeholder={t('policy.kpi.fields.label_ph') || 'Optional description'}
                    />
                  </label>
                  <label className="flex flex-col text-xs w-28">
                    <span className="font-medium mb-1">{t('policy.kpi.fields.severity') || 'Severity'}</span>
                    <select
                      className="input"
                      value={rule.severity}
                      onChange={e => updateKpiRule(rule.id, { severity: e.target.value as KpiRuleDraft['severity'] })}
                      disabled={!has('policies.write')}
                    >
                      <option value="info">{t('policy.kpi.severity.info') || 'Info'}</option>
                      <option value="warning">{t('policy.kpi.severity.warning') || 'Warning'}</option>
                      <option value="critical">{t('policy.kpi.severity.critical') || 'Critical'}</option>
                    </select>
                  </label>
                  <label className="flex flex-col text-xs w-28">
                    <span className="font-medium mb-1">{t('policy.kpi.fields.cooldown') || 'Cooldown (min)'}</span>
                    <input
                      className="input"
                      value={rule.cooldownMinutes}
                      onChange={e => updateKpiRule(rule.id, { cooldownMinutes: e.target.value })}
                      disabled={!has('policies.write')}
                      placeholder="0"
                    />
                  </label>
                  <button
                    type="button"
                    className="btn-secondary px-3 self-start"
                    onClick={() => removeKpiRule(rule.id)}
                    disabled={!has('policies.write')}
                    aria-label={t('buttons.remove') || 'Remove'}
                  >
                    {t('buttons.remove') || 'Remove'}
                  </button>
                </div>
                {rule.type === 'size_change' && (
                  <div className="flex flex-wrap gap-3 text-xs">
                    <label className="flex flex-col">
                      <span className="font-medium mb-1">{t('policy.kpi.fields.threshold_percent') || 'Threshold %'}</span>
                      <input
                        className="input w-28"
                        value={rule.thresholdPercent}
                        onChange={e => updateKpiRule(rule.id, { thresholdPercent: e.target.value })}
                        disabled={!has('policies.write')}
                      />
                    </label>
                    <label className="flex flex-col">
                      <span className="font-medium mb-1">{t('policy.kpi.fields.window') || 'Consecutive samples'}</span>
                      <input
                        className="input w-28"
                        value={rule.window}
                        onChange={e => updateKpiRule(rule.id, { window: e.target.value })}
                        disabled={!has('policies.write')}
                      />
                    </label>
                    <label className="flex flex-col">
                      <span className="font-medium mb-1">{t('policy.kpi.fields.direction') || 'Direction'}</span>
                      <select
                        className="input w-32"
                        value={rule.direction}
                        onChange={e => updateKpiRule(rule.id, { direction: e.target.value as KpiRuleDraft['direction'] })}
                        disabled={!has('policies.write')}
                      >
                        <option value="increase">{t('policy.kpi.direction.increase') || 'Increase'}</option>
                        <option value="decrease">{t('policy.kpi.direction.decrease') || 'Decrease'}</option>
                        <option value="both">{t('policy.kpi.direction.both') || 'Both'}</option>
                      </select>
                    </label>
                  </div>
                )}
                {rule.type === 'failure_streak' && (
                  <div className="flex flex-wrap gap-3 text-xs">
                    <label className="flex flex-col">
                      <span className="font-medium mb-1">{t('policy.kpi.fields.threshold_fail') || 'Consecutive failures'}</span>
                      <input
                        className="input w-28"
                        value={rule.threshold}
                        onChange={e => updateKpiRule(rule.id, { threshold: e.target.value })}
                        disabled={!has('policies.write')}
                      />
                    </label>
                    <label className="flex flex-col">
                      <span className="font-medium mb-1">{t('policy.kpi.fields.resume') || 'Auto-resume (min)'}</span>
                      <input
                        className="input w-28"
                        value={rule.resumeMinutes}
                        onChange={e => updateKpiRule(rule.id, { resumeMinutes: e.target.value })}
                        disabled={!has('policies.write')}
                      />
                    </label>
                  </div>
                )}
                {rule.type === 'last_full_age' && (
                  <div className="flex flex-wrap gap-3 text-xs">
                    <label className="flex flex-col">
                      <span className="font-medium mb-1">{t('policy.kpi.fields.max_age_hours') || 'Max hours since full'}</span>
                      <input
                        className="input w-32"
                        value={rule.maxAgeHours}
                        onChange={e => updateKpiRule(rule.id, { maxAgeHours: e.target.value })}
                        disabled={!has('policies.write')}
                      />
                    </label>
                  </div>
                )}
                <div className="flex flex-wrap gap-4 text-xs">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rule.actionForceFull}
                      onChange={e => updateKpiRule(rule.id, { actionForceFull: e.target.checked })}
                      disabled={!has('policies.write')}
                    />
                    {t('policy.kpi.actions.force_full') || 'Force full backup'}
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rule.actionPause}
                      onChange={e => updateKpiRule(rule.id, { actionPause: e.target.checked })}
                      disabled={!has('policies.write')}
                    />
                    {t('policy.kpi.actions.pause') || 'Auto pause schedule'}
                  </label>
                  {rule.actionPause && (
                    <input
                      className="input w-28"
                      value={rule.resumeMinutes}
                      onChange={e => updateKpiRule(rule.id, { resumeMinutes: e.target.value })}
                      disabled={!has('policies.write')}
                      placeholder={t('policy.kpi.fields.resume') || 'Resume (min)'}
                    />
                  )}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rule.actionExtendKeep}
                      onChange={e => updateKpiRule(rule.id, { actionExtendKeep: e.target.checked })}
                      disabled={!has('policies.write')}
                    />
                    {t('policy.kpi.actions.extend_keep') || 'Extend keep-last'}
                  </label>
                  {rule.actionExtendKeep && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <input
                        className="input w-24"
                        value={rule.extendKeepMin}
                        onChange={e => updateKpiRule(rule.id, { extendKeepMin: e.target.value })}
                        disabled={!has('policies.write')}
                        placeholder={t('policy.kpi.fields.keep_min') || 'Min'}
                      />
                      <input
                        className="input w-24"
                        value={rule.extendKeepIncrease}
                        onChange={e => updateKpiRule(rule.id, { extendKeepIncrease: e.target.value })}
                        disabled={!has('policies.write')}
                        placeholder={t('policy.kpi.fields.keep_inc') || '+n'}
                      />
                      <input
                        className="input w-28"
                        value={rule.extendKeepDuration}
                        onChange={e => updateKpiRule(rule.id, { extendKeepDuration: e.target.value })}
                        disabled={!has('policies.write')}
                        placeholder={t('policy.kpi.fields.keep_duration') || 'Hours'}
                      />
                    </div>
                  )}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rule.actionNotify}
                      onChange={e => updateKpiRule(rule.id, { actionNotify: e.target.checked })}
                      disabled={!has('policies.write')}
                    />
                    {t('policy.kpi.actions.notify') || 'Send notification'}
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-3">
          <button className="btn-secondary" type="button" onClick={() => addKpiRule('size_change')} disabled={!has('policies.write')}>
            {t('policy.kpi.add_size') || 'Add size rule'}
          </button>
          <button className="btn-secondary" type="button" onClick={() => addKpiRule('failure_streak')} disabled={!has('policies.write')}>
            {t('policy.kpi.add_failure') || 'Add failure rule'}
          </button>
          <button className="btn-secondary" type="button" onClick={() => addKpiRule('last_full_age')} disabled={!has('policies.write')}>
            {t('policy.kpi.add_full') || 'Add full-age rule'}
          </button>
        </div>
      </div>

      <div className="mt-6">

        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">

          <div>

            <h3 className="font-semibold text-sm">{t('policy.kpi.title') || 'Automazione KPI'}</h3>

            <p className="text-xs text-gray-600 dark:text-gray-400 max-w-2xl">

              {t('policy.kpi.help') ||

                'Definisci blocchi condizione/azione per reagire automaticamente ai KPI (dimensioni, fallimenti o eta dell\'ultimo full).'}

            </p>

          </div>

          <label className="flex flex-col text-xs min-w-[140px]">

            <span className="font-medium mb-1">

              {t('policy.kpi.fields.eval_interval') || 'Intervallo valutazione (min)'}

            </span>

            <input

              className="input"

              value={kpiEvaluationInterval}

              onChange={e => setKpiEvaluationInterval(e.target.value)}

              placeholder="es. 60"

              disabled={!has('policies.write')}

            />

          </label>

        </div>

        {kpiRules.length === 0 ? (

          <p className="text-xs text-gray-500">

            {t('policy.kpi.empty') || 'Nessuna regola configurata. Aggiungine una per iniziare.'}

          </p>

        ) : (

          <div className="space-y-4">

            {kpiRules.map(rule => {

              const typeLabel = KPI_RULE_LABELS[rule.type] || rule.type

              return (

                <div

                  key={rule.id}

                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900 shadow-sm"

                >

                  <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50">

                    <div className="flex flex-wrap items-center gap-2 text-xs">

                      <span className="uppercase tracking-wide text-gray-500">

                        {t('policy.kpi.fields.type') || 'Tipo'}

                      </span>

                      <select

                        className="input w-40"

                        value={rule.type}

                        onChange={e => updateKpiRule(rule.id, { type: e.target.value as KpiRuleDraft['type'] })}

                        disabled={!has('policies.write')}

                      >

                        <option value="size_change">{t('policy.kpi.type.size') || KPI_RULE_LABELS.size_change}</option>

                        <option value="failure_streak">{t('policy.kpi.type.failure') || KPI_RULE_LABELS.failure_streak}</option>

                        <option value="last_full_age">{t('policy.kpi.type.last_full') || KPI_RULE_LABELS.last_full_age}</option>

                      </select>

                      <span className="text-gray-400">/</span>

                      <span className="text-gray-600 dark:text-gray-300 font-semibold">{typeLabel}</span>

                    </div>

                    <button

                      type="button"

                      className="btn-secondary px-3"

                      onClick={() => removeKpiRule(rule.id)}

                      disabled={!has('policies.write')}

                      aria-label={t('buttons.remove') || 'Remove'}

                    >

                      {t('buttons.remove') || 'Remove'}

                    </button>

                  </div>

                  <div className="grid gap-3 md:grid-cols-3 p-3">

                    <div className="space-y-2 rounded-md bg-gray-50 dark:bg-gray-800/50 p-3">

                      <span className="text-[11px] uppercase tracking-wide text-gray-500">

                        {t('policy.kpi.block.identity') || 'Identita'}

                      </span>

                      <label className="flex flex-col text-xs">

                        <span className="font-medium mb-1">

                          {t('policy.kpi.fields.label') || 'Etichetta'}

                        </span>

                        <input

                          className="input"

                          value={rule.label}

                          onChange={e => updateKpiRule(rule.id, { label: e.target.value })}

                          placeholder={t('policy.kpi.fields.label_placeholder') || 'es. Full ogni 30 giorni'}

                          disabled={!has('policies.write')}

                        />

                      </label>

                      <label className="flex flex-col text-xs">

                        <span className="font-medium mb-1">

                          {t('policy.kpi.fields.severity') || 'Severita'}

                        </span>

                        <select

                          className="input"

                          value={rule.severity}

                          onChange={e => updateKpiRule(rule.id, { severity: e.target.value as KpiRuleDraft['severity'] })}

                          disabled={!has('policies.write')}

                        >

                          {KPI_SEVERITY_OPTIONS.map(opt => (

                            <option key={opt.value} value={opt.value}>

                              {t(`policy.kpi.severity.${opt.value}` as const) || opt.label}

                            </option>

                          ))}

                        </select>

                      </label>

                      <label className="flex flex-col text-xs">

                        <span className="font-medium mb-1">

                          {t('policy.kpi.fields.cooldown') || 'Cooldown (min)'}

                        </span>

                        <input

                          className="input"

                          value={rule.cooldownMinutes}

                          onChange={e => updateKpiRule(rule.id, { cooldownMinutes: e.target.value })}

                          placeholder="0"

                          disabled={!has('policies.write')}

                        />

                      </label>

                    </div>

                    <div className="space-y-2 rounded-md bg-gray-50 dark:bg-gray-800/50 p-3">

                      <span className="text-[11px] uppercase tracking-wide text-gray-500">

                        {t('policy.kpi.block.conditions') || 'Condizione'}

                      </span>

                      {rule.type === 'size_change' && (

                        <div className="space-y-2 text-xs">

                          <label className="flex flex-col">

                            <span className="font-medium mb-1">

                              {t('policy.kpi.fields.threshold_percent') || 'Soglia variazione (%)'}

                            </span>

                            <input

                              className="input"

                              value={rule.thresholdPercent}

                              onChange={e => updateKpiRule(rule.id, { thresholdPercent: e.target.value })}

                              disabled={!has('policies.write')}

                            />

                          </label>

                          <label className="flex flex-col">

                            <span className="font-medium mb-1">

                              {t('policy.kpi.fields.window') || 'Campioni consecutivi'}

                            </span>

                            <input

                              className="input"

                              value={rule.window}

                              onChange={e => updateKpiRule(rule.id, { window: e.target.value })}

                              disabled={!has('policies.write')}

                            />

                          </label>

                          <label className="flex flex-col">

                            <span className="font-medium mb-1">

                              {t('policy.kpi.fields.direction') || 'Direzione'}

                            </span>

                            <select

                              className="input"

                              value={rule.direction}

                              onChange={e => updateKpiRule(rule.id, { direction: e.target.value as KpiRuleDraft['direction'] })}

                              disabled={!has('policies.write')}

                            >

                              {KPI_DIRECTION_OPTIONS.map(opt => (

                                <option key={opt.value} value={opt.value}>

                                  {t(`policy.kpi.direction.${opt.value}` as const) || opt.label}

                                </option>

                              ))}

                            </select>

                          </label>

                        </div>

                      )}

                      {rule.type === 'failure_streak' && (

                        <div className="space-y-2 text-xs">

                          <label className="flex flex-col">

                            <span className="font-medium mb-1">

                              {t('policy.kpi.fields.threshold_fail') || 'Fallimenti consecutivi'}

                            </span>

                            <input

                              className="input"

                              value={rule.threshold}

                              onChange={e => updateKpiRule(rule.id, { threshold: e.target.value })}

                              disabled={!has('policies.write')}

                            />

                          </label>

                          <label className="flex flex-col">

                            <span className="font-medium mb-1">

                              {t('policy.kpi.fields.resume') || 'Ripresa automatica (min)'}

                            </span>

                            <input

                              className="input"

                              value={rule.resumeMinutes}

                              onChange={e => updateKpiRule(rule.id, { resumeMinutes: e.target.value })}

                              disabled={!has('policies.write')}

                            />

                          </label>

                        </div>

                      )}

                      {rule.type === 'last_full_age' && (

                        <div className="space-y-2 text-xs">

                          <label className="flex flex-col">

                            <span className="font-medium mb-1">

                              {t('policy.kpi.fields.max_age_hours') || 'Ore massime dall\'ultimo full'}

                            </span>

                            <input

                              className="input"

                              value={rule.maxAgeHours}

                              onChange={e => updateKpiRule(rule.id, { maxAgeHours: e.target.value })}

                              disabled={!has('policies.write')}

                            />

                          </label>

                        </div>

                      )}

                      {rule.type !== 'size_change' && rule.type !== 'failure_streak' && rule.type !== 'last_full_age' && (

                        <p className="text-xs text-gray-500">

                          {t('policy.kpi.fields.no_conditions') || 'Nessuna condizione aggiuntiva.'}

                        </p>

                      )}

                    </div>

                    <div className="space-y-2 rounded-md bg-gray-50 dark:bg-gray-800/50 p-3">

                      <span className="text-[11px] uppercase tracking-wide text-gray-500">

                        {t('policy.kpi.block.actions') || 'Azioni'}

                      </span>

                      <label className="flex items-center gap-2 text-xs">

                        <input

                          type="checkbox"

                          checked={rule.actionForceFull}

                          onChange={e => updateKpiRule(rule.id, { actionForceFull: e.target.checked })}

                          disabled={!has('policies.write')}

                        />

                        {t('policy.kpi.actions.force_full') || 'Forza un backup full'}

                      </label>

                      <div className="space-y-2 text-xs">

                        <label className="flex items-center gap-2">

                          <input

                            type="checkbox"

                            checked={rule.actionPause}

                            onChange={e => updateKpiRule(rule.id, { actionPause: e.target.checked })}

                            disabled={!has('policies.write')}

                          />

                          {t('policy.kpi.actions.pause') || 'Metti in pausa la schedule'}

                        </label>

                        {rule.actionPause && (

                          <input

                            className="input"

                            value={rule.resumeMinutes}

                            onChange={e => updateKpiRule(rule.id, { resumeMinutes: e.target.value })}

                            disabled={!has('policies.write')}

                            placeholder={t('policy.kpi.fields.resume') || 'Riprendi dopo (min)'}

                          />

                        )}

                      </div>

                      <div className="space-y-2 text-xs">

                        <label className="flex items-center gap-2">

                          <input

                            type="checkbox"

                            checked={rule.actionExtendKeep}

                            onChange={e => updateKpiRule(rule.id, { actionExtendKeep: e.target.checked })}

                            disabled={!has('policies.write')}

                          />

                          {t('policy.kpi.actions.extend_keep') || 'Estendi keep-last'}

                        </label>

                        {rule.actionExtendKeep && (

                          <div className="grid gap-2 sm:grid-cols-3">

                            <input

                              className="input"

                              value={rule.extendKeepMin}

                              onChange={e => updateKpiRule(rule.id, { extendKeepMin: e.target.value })}

                              disabled={!has('policies.write')}

                              placeholder={t('policy.kpi.fields.keep_min') || 'Min'}

                            />

                            <input

                              className="input"

                              value={rule.extendKeepIncrease}

                              onChange={e => updateKpiRule(rule.id, { extendKeepIncrease: e.target.value })}

                              disabled={!has('policies.write')}

                              placeholder={t('policy.kpi.fields.keep_inc') || '+n'}

                            />

                            <input

                              className="input"

                              value={rule.extendKeepDuration}

                              onChange={e => updateKpiRule(rule.id, { extendKeepDuration: e.target.value })}

                              disabled={!has('policies.write')}

                              placeholder={t('policy.kpi.fields.keep_duration') || 'Ore'}

                            />

                          </div>

                        )}

                      </div>

                      <label className="flex items-center gap-2 text-xs">

                        <input

                          type="checkbox"

                          checked={rule.actionNotify}

                          onChange={e => updateKpiRule(rule.id, { actionNotify: e.target.checked })}

                          disabled={!has('policies.write')}

                        />

                        {t('policy.kpi.actions.notify') || 'Invia notifica'}

                      </label>

                    </div>

                  </div>

                </div>

              )

            })}

          </div>

        )}

        <div className="flex flex-wrap gap-2 mt-3">

          <button className="btn-secondary" type="button" onClick={() => addKpiRule('size_change')} disabled={!has('policies.write')}>

            {t('policy.kpi.add_size') || 'Aggiungi regola variazione'}

          </button>

          <button className="btn-secondary" type="button" onClick={() => addKpiRule('failure_streak')} disabled={!has('policies.write')}>

            {t('policy.kpi.add_failure') || 'Aggiungi regola failure'}

          </button>

          <button className="btn-secondary" type="button" onClick={() => addKpiRule('last_full_age')} disabled={!has('policies.write')}>

            {t('policy.kpi.add_full') || 'Aggiungi regola eta full'}

          </button>

        </div>

      </div>

<div className="mt-6">
        <h3 className="font-semibold text-sm mb-2">{t('policy.freeze.title') || 'Freeze windows'}</h3>
        {freezeWindows.length === 0 ? (
          <p className="text-xs text-gray-500">{t('policy.freeze.empty') || 'No freeze windows configured.'}</p>
        ) : (
          <div className="space-y-3">
            {freezeWindows.map(win => (
              <div key={win.id} className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2">
                <div className="flex flex-wrap gap-3 text-xs">
                  <label className="flex flex-col min-w-[160px]">
                    <span className="font-medium mb-1">{t('policy.freeze.fields.days') || 'Days (e.g. mon-fri)'}</span>
                    <input
                      className="input"
                      value={win.days}
                      onChange={e => updateFreezeWindow(win.id, { days: e.target.value })}
                      disabled={!has('policies.write')}
                    />
                  </label>
                  <label className="flex flex-col">
                    <span className="font-medium mb-1">{t('policy.freeze.fields.start') || 'Start'}</span>
                    <input
                      type="time"
                      className="input"
                      value={win.start}
                      onChange={e => updateFreezeWindow(win.id, { start: e.target.value })}
                      disabled={!has('policies.write')}
                    />
                  </label>
                  <label className="flex flex-col">
                    <span className="font-medium mb-1">{t('policy.freeze.fields.end') || 'End'}</span>
                    <input
                      type="time"
                      className="input"
                      value={win.end}
                      onChange={e => updateFreezeWindow(win.id, { end: e.target.value })}
                      disabled={!has('policies.write')}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn-secondary px-3 self-start"
                    onClick={() => removeFreezeWindow(win.id)}
                    disabled={!has('policies.write')}
                    aria-label={t('buttons.remove') || 'Remove'}
                  >
                    {t('buttons.remove') || 'Remove'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3">
          <button className="btn-secondary" type="button" onClick={addFreezeWindow} disabled={!has('policies.write')}>
            {t('policy.freeze.add') || 'Add freeze window'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {t('policy.freeze.hint') || 'Schedules are paused during these windows to avoid running backups.'}
        </p>
      </div>

      {!has('policies.write') && (
        <p className="text-xs text-gray-500 mt-3">
          You do not have permission to create policies.
        </p>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <table className="w-full text-sm mt-4">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <th className="text-left">{t('table.name')}</th>
              <th>{t('table.schedule')}</th>
              <th>{t('table.retention')}</th>
              <th>{t('policy.strategy') || 'Strategy'}</th>
              <th>{t('policy.max_non_full') || 'Max non-full'}</th>
              <th>{t('policy.full_interval') || 'Full interval'}</th>
              <th>{t('policy.kpi.column') || 'KPI rules'}</th>
              <th>{t('policy.kpi.latest') || 'Ultimo trigger KPI'}</th>
              <th>{t('policy.freeze.column') || 'Freeze windows'}</th>
              <th>{t('table.stateful')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-gray-200 dark:border-gray-700">
                <td>{p.name}</td>
                <td className="text-center">{p.schedule}</td>
                <td className="text-center">{p.retention}</td>
                <td className="text-center">{formatStrategy(p.backup_strategy)}</td>
                <td className="text-center">{typeof p.max_non_full_backups === 'number' ? p.max_non_full_backups : '-'}</td>
                <td className="text-center">{typeof p.full_backup_interval === 'number' ? p.full_backup_interval : '-'}</td>
                <td className="text-center">{p.dynamic_retention?.rules?.length ?? 0}</td>
                <td>{renderPolicyKpiBadge(p.id)}</td>
                <td className="text-center">{p.freeze_windows?.length ?? 0}</td>
                <td className="text-center">{statefulSummary(p)}</td>
                <td className="text-right">
                  {has('policies.write') && (
                    <button className="btn-secondary" onClick={() => setConfirmId(p.id)}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="mr-2"
                        aria-hidden="true"
                      >
                        <path d="M7 6h10l-1 14H8L7 6Zm2-2h6l1 2H8l1-2Z" />
                      </svg>
                      {t('buttons.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Confirm
        open={!!confirmId}
        onClose={() => setConfirmId(undefined)}
        onConfirm={async () => {
          if (!confirmId) return
          try {
            const res = await fetch(`/policies/${confirmId}`, { method: 'DELETE', credentials: 'include' })
            if (!res.ok) alert(await res.text())
            await load()
          } finally {
            setConfirmId(undefined)
          }
        }}
      >
        Delete this policy? (must have no schedules)
      </Confirm>
    </div>
  )
}








